import React, { useMemo } from "react";

import { css, cx } from "@emotion/css";
import { useHover } from "@uidotdev/usehooks";

import { useStyles } from "@/hooks/useStyles.ts";
import { Message, isMessageRead, isMessageSender } from "@/models/MessagingModel.ts";
import { PractitionerUser } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { MessageAttachments } from "@/uiKit/molecules/chat/MessageCard/MessageAttachments.tsx";
import { MessageReadReceipt } from "@/uiKit/molecules/chat/MessageCard/MessageReadReceipt.tsx";
import { MessageText } from "@/uiKit/molecules/chat/MessageCard/MessageText.tsx";
import { MessageTitle } from "@/uiKit/molecules/chat/MessageCard/MessageTitle.tsx";

import { DeleteMessage } from "./DeleteMessage";
import { MessageReactionBar } from "./MessageReactionBar.tsx";
import { MessageReactions } from "./MessageReactions.tsx";

export type MessageCardProps = {
  message: Message;
  className?: string;
};

const useMessageReadStatus = (me: PractitionerUser, message: Message) => {
  return useMemo(() => {
    const readReceiptList = message.readReceipts.filter(r => r.read);
    const whoHasRead: "everybody" | "some" | "nobody" =
      readReceiptList.length === message.readReceipts.length
        ? "everybody"
        : readReceiptList.length > 0
          ? "some"
          : "nobody";
    return {
      userIsSender: isMessageSender(message, me.id),
      userHasNotRead: !isMessageRead(message, me.id),
      forPractitionersOnly: message.visibility === "practitioners",
      readReceiptList,
      whoHasRead,
    };
  }, [me, message]);
};

export const MessageCard: React.FC<MessageCardProps> = ({ message, className }) => {
  const styles = useStyles(makeStyles);
  const [ref, hovering] = useHover();
  const me = Queries.user.usePractitionerMe({});
  const status = useMessageReadStatus(me, message);

  return (
    <div
      id={`message-${message.id}`}
      className={cx(styles.container, className, {
        [styles.senderContainer]: status.userIsSender,
      })}
      ref={ref}
    >
      <DeleteMessage message={message} hovering={hovering} userIsSender={status.userIsSender} />
      <Card
        className={cx(styles.card, className, {
          [styles.unreadCard]: status.userHasNotRead,
          [styles.practitionerOnlyCard]: status.forPractitionersOnly,
          [styles.senderCard]: status.userIsSender,
        })}
      >
        <MessageTitle message={message} userIsSender={status.userIsSender} />
        <MessageText message={message} />
        <MessageAttachments message={message} />
        <MessageReactions
          isPractitionerOnly={status.forPractitionersOnly}
          isSender={status.userIsSender}
          className={styles.reactions}
          message={message}
          meId={me.id}
        />
        <MessageReadReceipt
          message={message}
          whoHasRead={status.whoHasRead}
          readReceiptList={status.readReceiptList}
          hovering={hovering}
        />
      </Card>
      {!status.userIsSender && <MessageReactionBar message={message} hovering={hovering} meId={me.id} />}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  }),
  senderContainer: css({
    justifyContent: "flex-end",
  }),
  card: css({
    overflow: "visible",
    display: "flex",
    position: "relative",
    flexDirection: "column",
    flex: "1 1 auto",
    minWidth: 0,
    maxWidth: "85%",
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0, 4, 4, 4),
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    ">:last-child": {
      paddingBottom: theme.spacing(0),
    },
  }),
  practitionerOnlyCard: css({
    backgroundColor: theme.palette.green.contrastText,
  }),
  senderCard: css({
    borderRadius: theme.spacing(4, 4, 0, 4),
  }),
  unreadCard: css({
    border: "1px solid",
    borderColor: `rgb(from ${theme.palette.primary.main} r g b / 0.2)`,
  }),
  reactions: css({
    position: "absolute",
    bottom: theme.spacing(-4),
    left: theme.spacing(4),
    zIndex: 1,
  }),
});
